import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Stack,
  Alert,
  Divider,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { api } from "../../utils/api";

const BUSINESS_TYPES = [
  "AOP (Association of Persons)",
  "BOI (Body of Individuals)",
  "Co-operative Society",
  "Educational Institution",
  "Government Department",
  "HUF (Hindu Undivided Family)",
  "Hospital / Healthcare",
  "Joint Venture",
  "LLP",
  "Local Authority",
  "MSME",
  "NGO",
  "OPC",
  "Partnership",
  "Proprietorship",
  "Public Ltd",
  "Pvt Ltd",
  "Real Estate / Builder",
  "Section 8 Company",
  "Society",
  "Startup",
  "Statutory Body",
  "Trust",
  "Unregistered",
];

const STATES = [
  "Andaman & Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra & Nagar Haveli & Daman & Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const REQUIRED = ["business_name", "contact_person", "mobile", "business_type", "city", "state", "pincode"];

export default function AddClientForm({ onSuccess, onClose }) {
  const [form, setForm] = useState({
    business_name: "",
    contact_person: "",
    email: "",
    mobile: "",
    whatsapp: "",
    business_type: "",
    gstin: "",
    pan: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setForm((old) => ({ ...old, [key]: value }));
    if (errors[key]) setErrors((old) => ({ ...old, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    REQUIRED.forEach((f) => {
      if (!form[f] || String(form[f]).trim() === "")
        e[f] = "Required";
    });
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (form.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(form.gstin))
      e.gstin = "Invalid GSTIN";
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan))
      e.pan = "Invalid PAN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post("/clients/create.php", form);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to add client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ pb: 1 }}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        Basic Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Business Name *"
            value={form.business_name}
            onChange={(e) => setField("business_name", e.target.value)}
            error={!!errors.business_name}
            helperText={errors.business_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Contact Person *"
            value={form.contact_person}
            onChange={(e) => setField("contact_person", e.target.value)}
            error={!!errors.contact_person}
            helperText={errors.contact_person}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Mobile *"
            inputProps={{ maxLength: 10 }}
            value={form.mobile}
            onChange={(e) => setField("mobile", e.target.value)}
            error={!!errors.mobile}
            helperText={errors.mobile}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="Email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="WhatsApp"
            value={form.whatsapp}
            onChange={(e) => setField("whatsapp", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth label="Business Type *"
            value={form.business_type}
            onChange={(e) => setField("business_type", e.target.value)}
            error={!!errors.business_type}
            helperText={errors.business_type}
          >
            <MenuItem value="">Select business type</MenuItem>
            {BUSINESS_TYPES.map((b) => (
              <MenuItem key={b} value={b}>{b}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="GSTIN"
            value={form.gstin}
            onChange={(e) => setField("gstin", e.target.value.toUpperCase())}
            error={!!errors.gstin}
            helperText={errors.gstin}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth label="PAN"
            value={form.pan}
            onChange={(e) => setField("pan", e.target.value.toUpperCase())}
            error={!!errors.pan}
            helperText={errors.pan}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        Registered Address
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth multiline rows={2}
            label="Full Address"
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth label="City *"
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth label="State *"
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
            error={!!errors.state}
            helperText={errors.state}
          >
            <MenuItem value="">Select state</MenuItem>
            {STATES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth label="Pincode *"
            value={form.pincode}
            onChange={(e) => setField("pincode", e.target.value)}
            error={!!errors.pincode}
            helperText={errors.pincode}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: "flex-end" }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<AddCircleIcon />}
          disabled={saving}
        >
          {saving ? "Adding…" : "Add Client"}
        </Button>
      </Stack>
    </Box>
  );
}
